/** @jsxImportSource @emotion/react */

import { useEffect, useState } from 'react';

interface IIdleDetector {
  enabled: boolean;
  timeToIdle: number;
}

const ACTIVITY_EVENTS = ['load', 'click', 'keydown', 'scroll'];

// 'visibilitychange' stops the idle countdown (and any running idle warning) when
// the tab is hidden, and starts a fresh one when the user returns. It fires on
// document, so it is registered there instead of on window.
const DOCUMENT_ACTIVITY_EVENTS = ['visibilitychange'];
let idleTimeout: number;

const useIdleDetector = ({ enabled, timeToIdle }: IIdleDetector) => {
  const [userIsIdle, setUserIsIdle] = useState(false);

  // Setup user activity event listeners.
  useEffect(() => {
    // On user activity, reset the current timeout and set a new one.
    const onUserActivity = () => {
      setUserIsIdle(false);
      clearTimeout(idleTimeout);

      // Only count while the tab is visible, so the patient is never logged out
      // from a hidden tab. Events like 'load' can still fire while hidden.
      if (enabled && !document.hidden) {
        idleTimeout = window.setTimeout(() => {
          setUserIsIdle(true);
        }, timeToIdle * 1000);
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onUserActivity, true);
    });
    DOCUMENT_ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, onUserActivity, true);
    });

    onUserActivity();

    // Clear timeout and remove event listeners.
    return () => {
      clearTimeout(idleTimeout);

      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onUserActivity, true);
      });
      DOCUMENT_ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, onUserActivity, true);
      });
    };
  }, [enabled, timeToIdle]);

  return {
    userIsIdle,
  };
};

export default useIdleDetector;
